import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const PhilosophyPage = () => {
  const [currentChapter, setCurrentChapter] = useState(0);

  useSEO({
    title: '理念 — 言葉で描く「静かな伴走」 | 2b LLC',
    description: '2bの根にあるのは語らずとも伝わる力。言葉を少なく誠実を多く。静かな伴走で誰かの思いを形にする会社の理念をご紹介します。',
    keywords: '2b LLC,理念,静かな伴走,企業哲学,ブランドワードブック',
    canonical: getSiteUrl('/philosophy'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '理念',
      description: '2b LLCの理念とブランドワードブック',
      url: getSiteUrl('/philosophy'),
      mainEntity: {
        '@type': 'Organization',
        name: '合同会社2b',
        slogan: '静かな伴走、そっと前へ',
        description: '語らずとも伝わる力。言葉を少なく誠実を多く。'
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />

      <main className="pt-32">
        {/* ---------- ロゴセクション ---------- */}
        <section
          className="py-12 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/12e4424d5b2aaebd545598b61604cd28.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation delay={0}>
              <img
                src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
                alt="2b LLC"
                className="w-[480px] h-auto mx-auto mb-12 object-contain"
              />
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- ヒーローセクション ---------- */}
        <section
          className="py-20 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Soft%20ripples%20expanding%20on%20calm%20water%20surface%2C%20peaceful%20aqua%20blue%20background%20with%20gentle%20waves%2C%20serene%20water%20texture%20with%20concentric%20circles%2C%20minimalist%20zen-like%20composition%20perfect%20for%20text%20overlay%2C%20tranquil%20and%20meditative%20atmosphere&width=1920&height=1080&seq=philosophy-hero-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/80"></div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation delay={300}>
              <h1
                className="text-4xl md:text-5xl font-semibold text-[#4A4A48] mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                2b Brand Wordbook
              </h1>
            </FadeUpAnimation>

            <FadeUpAnimation delay={600}>
              <p
                className="text-xl text-[#4A4A48] opacity-80"
                style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
              >
                — 言葉で描く「静かな伴走」
              </p>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- 序文 ---------- */}
        <section
          className="py-24 bg-white relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Gentle%20water%20ripples%20creating%20concentric%20patterns%2C%20soft%20blue-tinted%20water%20surface%20with%20peaceful%20waves%2C%20serene%20aquatic%20background%20with%20subtle%20reflections%2C%20clean%20minimalist%20composition%20perfect%20for%20text%20content%2C%20zen-like%20tranquil%20atmosphere&width=1920&height=1080&seq=philosophy-intro-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center space-y-8">
                <p className="text-lg md:text-3xl text-[#4A4A48] leading-[1.75] font-medium" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                  2bの根にあるのは<br />
                  "語らずとも伝わる力"。<br />
                </p>

                <div className="space-y-6">
                  <p className="text-base md:text-xl text-[#4A4A48] leading-[1.75]" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                    言葉を少なく、配慮を多く。<br />
                    見過ごされるものの中にこそ、静かな答えがある。<br />
                  </p>

                  <p className="text-sm md:text-xl text-[#4A4A48] leading-[1.75] opacity-80" style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}>
                    このページは、私たちの仕事と言葉をつなぐ小さな辞書のようなものです。<br />
                  </p>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Chapter 1 ---------- */}
        <section
          className="py-24 bg-white relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Calm%20water%20surface%20with%20gentle%20ripples%20spreading%20outward%2C%20peaceful%20blue%20water%20with%20soft%20wave%20patterns%2C%20serene%20aquatic%20texture%20with%20concentric%20circles%2C%20minimalist%20zen%20composition%20perfect%20background%20for%20content%20sections%2C%20tranquil%20meditative%20atmosphere&width=1920&height=1080&seq=philosophy-ch1-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center mb-16">
                <h2
                  className="text-3xl font-semibold text-[#4A4A48] mb-8 pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Chapter 1:<br />
                  静けさの中の意思
                </h2>
                <p
                  className="text-base md:text-xl text-[#4A4A48] leading-[1.75] max-w-3xl mx-auto"
                  style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                >
                  <br />
                  言葉よりも、背中で示す。<br />
                  主張せず、しかし揺るがない。<br />
                  静けさには、前へ進める意思が宿る。<br />
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation delay={400}>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    静かな伴走
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    穏やかな推進力
                  </h3>
                </div>
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    音を立てないリーダーシップ
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    静けさの中の力
                  </h3>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Chapter 2 ---------- */}
        <section
          className="py-24 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Soft%20water%20ripples%20expanding%20in%20gentle%20circles%2C%20peaceful%20aqua%20blue%20surface%20with%20subtle%20wave%20patterns%2C%20serene%20water%20background%20with%20light%20reflections%2C%20minimalist%20zen-like%20composition%20perfect%20for%20content%20overlay%2C%20calm%20and%20harmonious%20atmosphere&width=1920&height=1080&seq=philosophy-ch2-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/80"></div>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center mb-16">
                <h2
                  className="text-3xl font-semibold text-[#4A4A48] mb-8 pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Chapter 2:<br />
                  寄り添う技術
                </h2>
                <p
                  className="text-base md:text-xl text-[#4A4A48] leading-[1.75] max-w-3xl mx-auto"
                  style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                >
                  <br />
                  寄り添いは感情ではなく設計。<br />
                  相手の呼吸に合わせ、歩幅を整える。<br />
                  それがプロの伴走者の仕事。<br />
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation delay={400}>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/70 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    そっと寄り添う力
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    見えないところで、整える人
                  </h3>
                </div>
                <div className="bg-white/70 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    風のようの伴走者
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    誰かの一歩を、後ろから照らす
                  </h3>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Chapter 3 ---------- */}
        <section
          className="py-24 bg-white relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Gentle%20concentric%20ripples%20on%20calm%20water%20surface%2C%20soft%20blue-tinted%20aquatic%20background%20with%20peaceful%20waves%2C%20serene%20water%20texture%20with%20subtle%20light%20reflections%2C%20minimalist%20zen%20composition%20perfect%20for%20text%20sections%20tranquil%20and%20meditative%20atmosphere&width=1920&height=1080&seq=philosophy-ch3-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center mb-16">
                <h2
                  className="text-3xl font-semibold text-[#4A4A48] mb-8 pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Chapter 3:<br />
                  見えない推進力
                </h2>
                <p
                  className="text-base md:text-xl text-[#4A4A48] leading-[1.75] max-w-3xl mx-auto"
                  style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                >
                  <br />
                  和によって、目立たぬまま動く。<br />
                  静かな場づくりで、チームは前に進む。<br />
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation delay={400}>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    影の優しさ
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    支えることで、世界を動かす
                  </h3>
                </div>
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    動かぬ心で、動かす世界
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    見えないところで、動かす人
                  </h3>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Chapter 4 ---------- */}
        <section
          className="py-24 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Peaceful%20water%20ripples%20spreading%20across%20calm%20surface%2C%20soft%20aqua%20blue%20background%20with%20gentle%20wave%20patterns%2C%20serene%20aquatic%20texture%20with%20concentric%20circles%20minimalist%20zen-like%20composition%20perfect%20for%20content%20overlay%20harmonious%20and%20tranquil%20atmosphere&width=1920&height=1080&seq=philosophy-ch4-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/80"></div>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center mb-16">
                <h2
                  className="text-3xl font-semibold text-[#4A4A48] mb-8 pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Chapter 4:<br />
                  人の間(ま)に生きる
                </h2>
                <p
                  className="text-base md:text-xl text-[#4A4A48] leading-[1.75] max-w-3xl mx-auto"
                  style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                >
                  <br />
                  人と人の"あいだ"に風を通す。<br />
                  感情を否定せず、流れを整える。<br />
                  その静けさが、合意と前進を生む。<br />
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation delay={400}>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/70 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    和をもって貴しとなす
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    声にならない支え
                  </h3>
                </div>
                <div className="bg-white/70 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    風が通るように、場を整える
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    ひと呼吸、先を見つめて
                  </h3>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Chapter 5 ---------- */}
        <section
          className="py-24 bg-white relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Soft%20water%20ripples%20creating%20gentle%20patterns%2C%20peaceful%20blue%20water%20surface%20with%20subtle%20waves%2C%20serene%20aquatic%20background%20with%20light%20reflections%2C%20clean%20minimalist%20composition%20perfect%20for%20text%20content%2C%20zen-like%20tranquil%20atmosphere&width=1920&height=1080&seq=philosophy-ch5-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="text-center mb-16">
                <h2
                  className="text-3xl font-semibold text-[#4A4A48] mb-8 pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Chapter 5:<br />
                  小さな光、大きな余白
                </h2>
                <p
                  className="text-base md:text-xl text-[#4A4A48] leading-[1.75] max-w-3xl mx-auto"
                  style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                >
                  <br />
                  焦らず、慌てず、ゆっくりと。<br />
                  安心が芽吹けば、希望は自然と育つ。<br />
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation delay={400}>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    そばにいて、未来を押す
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    寄り添いながら、動かす
                  </h3>
                </div>
                <div className="bg-[#F6F3EB]/50 p-8 rounded-xl">
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    あなたの "to be" を支える手
                  </h3>
                  <h3
                    className="text-lg md:text-2xl font-medium text-[#4A4A48] mb-4"
                    style={{ fontFamily: 'Zen Kaku Gothic New, sans-serif' }}
                  >
                    気づけば、うまくいっている
                  </h3>
                </div>
              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- Epilogue ---------- */}
        <section
          className="py-24 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Gentle%20concentric%20ripples%20expanding%20on%20calm%20water%2C%20soft%20aqua%20blue%20surface%20with%20peaceful%20wave%20patterns%20serene%20water%20background%20with%20subtle%20reflections%20minimalist%20zen%20composition%20perfect%20for%20epilogue%20section%20tranquil%20and%20contemplative%20atmosphere&width=1920&height=1080&seq=philosophy-epilogue-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/80"></div>
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <FadeUpAnimation delay={200}>
              <div className="space-y-16">
                <FadeUpAnimation>
                  <div className="text-center">
                    <p
                      className="text-base md:text-2xl text-[#4A4A48] leading-[2.0] md:leading-[2.2] font-light"
                      style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                    >
                      変わりたいのに、動けない。<br />
                      そんな誰かの背中を、静かに押せる存在でありたい。<br />
                    </p>
                  </div>
                </FadeUpAnimation>

                <div className="w-16 h-px bg-[#D7D5D0] mx-auto"></div>

                <FadeUpAnimation>
                  <div className="text-center">
                    <p
                      className="text-base md:text-2xl text-[#4A4A48] leading-[2.0] md:leading-[2.2] font-light"
                      style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                    >
                      自分の幸せを、他者の幸せに繋ぐ。<br />
                    </p>
                  </div>
                </FadeUpAnimation>

                <div className="w-16 h-px bg-[#D7D5D0] mx-auto"></div>

                <FadeUpAnimation>
                  <div className="text-center">
                    <p
                      className="text-base md:text-2xl text-[#4A4A48] leading-[2.0] md:leading-[2.2] font-light"
                      style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                    >
                      それが、私たちの技術であり、心がけです。<br />
                    </p>
                  </div>
                </FadeUpAnimation>

              </div>
            </FadeUpAnimation>
          </div>
        </section>

        {/* ---------- CTA ---------- */}
        <section
          className="py-24 bg-[#4A4A48] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Dark%20water%20surface%20with%20gentle%20ripples%20and%20waves%2C%20deep%20blue-black%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20peaceful%20nighttime%20water%20with%20concentric%20circles%20serene%20and%20contemplative%20atmosphere%20perfect%20for%20dark%20themed%20CTA%20sections&width=1920&height=1080&seq=philosophy-cta-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#4A4A48]/90"></div>
          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
            <FadeUpAnimation delay={200}>
              <h2 className="text-lg md:text-xl font-semibold text-white mb-8">
                一緒に、静かな変化を。<br />
              </h2>
              <p className="text-sm md:text-base text-white/90 mb-12 max-w-2xl mx-auto">
                あなたの想いに寄り添い、静かに前へ進むお手伝いをさせてください。<br />
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-white text-[#4A4A48] px-8 py-4 rounded-lg font-medium hover:bg-[#F6F3EB] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </a>
            </FadeUpAnimation>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default PhilosophyPage;

